import { useMemo } from 'react'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { isArray } from 'lodash-es'

import {
  assignmentCostsQuery,
  assignmentDistancesQuery,
  assignmentExecutionsQuery,
  assignmentPlansQuery,
  assignmentQuery,
  assignmentsByGroupIdQuery,
  nextGlobalAssignmentQuery,
  previousGlobalAssignmentQuery,
} from '@/state/assignments/assignments'
import { type GlobalAssignment, type RegionAssignment } from '@/state/assignments/types'
import { regionsQuery } from '@/state/regions/regions'

export const useGlobalAssignment = (): GlobalAssignment | null => {
  const { assignmentId } = useParams({ from: '/assignments/$assignmentId' })

  const { data: assignmentSnapshot } = useSuspenseQuery(assignmentQuery(assignmentId))
  const { data: costsSnapshot } = useSuspenseQuery(assignmentCostsQuery(assignmentId))
  const { data: plansSnapshot } = useSuspenseQuery(assignmentPlansQuery(assignmentId))

  const { data: executionsSnapshot } = useQuery(assignmentExecutionsQuery(assignmentId))
  const { data: distancesSnapshot } = useQuery(assignmentDistancesQuery(assignmentId))

  const { data: regionsSnapshot } = useQuery(regionsQuery())

  const { data: previousSnapshot } = useQuery(previousGlobalAssignmentQuery(assignmentSnapshot.data?.timestamp))
  const { data: nextSnapshot } = useQuery(nextGlobalAssignmentQuery(assignmentSnapshot.data?.timestamp))

  const groupId = assignmentSnapshot.data?.groupId
  const { data: regionalAssignmentsSnapshot } = useQuery(assignmentsByGroupIdQuery(groupId))

  const assignmentData = assignmentSnapshot.data
  const costsData = costsSnapshot.data
  const plansData = plansSnapshot.data
  const regionsData = useMemo(() => regionsSnapshot?.data ?? [], [regionsSnapshot?.data])

  const regionIds = useMemo<string[]>(() => {
    const regionId = assignmentData?.regionId
    if (isArray(regionId)) {
      return regionId as string[]
    }
    return []
  }, [assignmentData?.regionId])
  const matchedRegions = useMemo(() => regionsData.filter((r) => regionIds.includes(r._id)), [regionsData, regionIds])

  const regionalAssignments = useMemo<RegionAssignment[]>(() => {
    if (!regionalAssignmentsSnapshot?.data) return []

    return regionalAssignmentsSnapshot.data.map((assignment) => {
      const regionId = typeof assignment.regionId === 'string' ? assignment.regionId : ''
      const region = regionsData.find((r) => r._id === regionId)

      return {
        _id: assignment._id,
        assignment,
        costs: {
          _id: '',
          formula: 'points',
          teamIds: [],
          transportRequestIds: [],
          costs: {},
          details: { teams: {}, transportRequests: {} },
        },
        region,
        solver: undefined,
        executions: [],
        distancesFetched: [],
        regionId,
        groupId: assignment.groupId,
        timestamp: assignment.timestamp,
        teams: assignment.teams,
        transportRequests: assignment.transportRequests,
        executePlan: assignment.executePlan,
      }
    })
  }, [regionalAssignmentsSnapshot?.data, regionsData])

  return useMemo<GlobalAssignment | null>(() => {
    if (!assignmentData || !costsData || !plansData || !groupId) {
      return null
    }

    return {
      _id: assignmentId,
      assignment: assignmentData,
      costs: costsData,
      regions: matchedRegions,
      solver: plansData.find((p) => p._id === 'solver'),
      executions: executionsSnapshot?.data ?? [],
      distancesFetched: distancesSnapshot?.data ?? [],
      previousId: previousSnapshot?.data?.[0]?._id,
      nextId: nextSnapshot?.data?.[0]?._id,
      regionIds,
      groupId,
      timestamp: assignmentData.timestamp,
      teams: assignmentData.teams,
      transportRequests: assignmentData.transportRequests,
      executePlan: assignmentData.executePlan,
      regionalAssignments,
    }
  }, [
    assignmentId,
    assignmentData,
    costsData,
    distancesSnapshot?.data,
    executionsSnapshot?.data,
    groupId,
    matchedRegions,
    nextSnapshot?.data,
    plansData,
    previousSnapshot?.data,
    regionIds,
    regionalAssignments,
  ])
}
